import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  beneficiairesApi,
  ApiError,
  type Beneficiaire,
  type BeneficiaireWrite,
  type ImportPreviewRow,
  type ImportResult,
} from '../services/api';
import { useToast } from '../context/ToastContext';
import { Spinner } from '../components/shared/Spinner';
// `String(err)` affiche « ApiError: … » et jette les `issues[]` d'un refus Zod.
// Cet écran porte les IBAN : quand le serveur refuse, l'utilisateur doit lire
// QUEL champ est en cause, pas un nom de classe JavaScript.
import { formatApiError } from '../lib/api-error';
import { VendorSelect } from '../components/shared/VendorSelect';
import { useZohoVendors } from '../hooks/useZohoVendors';
import { useDebounce, useZohoCustomersSearch } from '../hooks/useZohoCustomersSearch';
import { AGICAP_HEADERS, TEMPLATE_EXAMPLE_ROWS, buildBeneficiairesCsv } from '../lib/beneficiaires-csv';
import type { ZohoVendor } from '../types';

/** Déclenche le téléchargement d'un contenu texte sous forme de fichier. */
function downloadFile(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Module Paiements Fournisseurs — Phase 1 : gestion des bénéficiaires.
 * Liste + création/modification/suppression + import CSV (format AGICAP).
 */

function fmtIban(iban: string): string {
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

export function BeneficiairesPage() {
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | 'fournisseur' | 'client'>('');
  const [onlyNoVendor, setOnlyNoVendor] = useState(false);
  const [editing, setEditing] = useState<Beneficiaire | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['beneficiaires', q, typeFilter],
    queryFn: () => beneficiairesApi.list({ q: q || undefined, type: typeFilter || undefined }),
    staleTime: 30_000,
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => beneficiairesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['beneficiaires'] });
      qc.invalidateQueries({ queryKey: ['paiements'] });
      qc.invalidateQueries({ queryKey: ['remboursements'] });
      toast.success('Bénéficiaire supprimé.');
    },
    onError: (err) => toast.error(`Suppression échouée : ${formatApiError(err)}`),
  });

  const allRows = list.data?.beneficiaires ?? [];
  const rows = onlyNoVendor ? allRows.filter((b) => !b.vendor_id) : allRows;
  const noVendorCount = allRows.filter((b) => !b.vendor_id).length;

  // Export CSV (format AGICAP) de TOUS les bénéficiaires (pas la vue filtrée).
  async function exportCsv() {
    try {
      const all = await beneficiairesApi.list();
      if (all.beneficiaires.length === 0) {
        toast.info('Aucun bénéficiaire à exporter.');
        return;
      }
      const today = new Date().toISOString().slice(0, 10);
      downloadFile(`beneficiaires_${today}.csv`, buildBeneficiairesCsv(all.beneficiaires), 'text/csv;charset=utf-8');
    } catch (e) {
      toast.error(`Export échoué : ${formatApiError(e)}`);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bénéficiaires</h1>
          <p className="text-sm text-slate-500 mt-1">
            Coordonnées bancaires des fournisseurs, pour la préparation des
            virements SEPA.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportCsv}
            className="px-3 py-1.5 rounded-md text-sm text-slate-700 hover:bg-slate-100 border border-slate-200"
          >
            Exporter CSV
          </button>
          <button
            type="button"
            onClick={() => setImporting(true)}
            className="px-3 py-1.5 rounded-md text-sm text-slate-700 hover:bg-slate-100 border border-slate-200"
          >
            Importer
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700"
          >
            + Ajouter
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="search"
          placeholder="Rechercher (nom ou IBAN)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full max-w-sm rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as '' | 'fournisseur' | 'client')}
          className="rounded-md border border-slate-300 px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tous les types</option>
          <option value="fournisseur">Fournisseurs</option>
          <option value="client">Clients</option>
        </select>
        {list.isFetching && <Spinner />}
        <label className="flex items-center gap-1.5 text-sm text-slate-600 ml-auto whitespace-nowrap">
          <input type="checkbox" checked={onlyNoVendor} onChange={(e) => setOnlyNoVendor(e.target.checked)} />
          Sans fournisseur Zoho{noVendorCount > 0 ? ` (${noVendorCount})` : ''}
        </label>
        <span className="text-sm text-slate-500">{rows.length} bénéficiaire(s)</span>
      </div>

      {list.isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : list.isError ? (
        <p className="text-[#C0423F]">Erreur : {formatApiError(list.error)}</p>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2 font-medium">Nom</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Lien Zoho</th>
                <th className="px-4 py-2 font-medium">IBAN</th>
                <th className="px-4 py-2 font-medium">BIC</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">{b.vendor_name}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                        b.type === 'client'
                          ? 'bg-purple-100 text-purple-800 ring-purple-200'
                          : 'bg-blue-100 text-blue-800 ring-blue-200'
                      }`}
                    >
                      {b.type === 'client' ? 'Client' : 'Fournisseur'}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {b.vendor_id ? (
                      <span className="text-xs text-emerald-700">✓ lié</span>
                    ) : (
                      <span className="text-xs text-amber-600">⚠ non lié</span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{fmtIban(b.iban)}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">{b.bic ?? '-'}</td>
                  <td className="px-4 py-2 text-slate-600">{b.bank_name ?? '-'}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setEditing(b)}
                      className="text-blue-600 hover:underline text-xs mr-3"
                    >
                      Modifier
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Supprimer ${b.vendor_name} ?`)) removeMutation.mutate(b.id);
                      }}
                      className="text-red-600 hover:underline text-xs"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400 italic">
                    Aucun bénéficiaire. Importe un CSV AGICAP ou ajoute-en un.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <BeneficiaireFormModal
          existing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {importing && <ImportModal onClose={() => setImporting(false)} />}
    </div>
  );
}

// ─── Modal création / modification ───────────────────────────────────────────

const EMPTY: BeneficiaireWrite = {
  type: 'fournisseur',
  vendor_id: null,
  vendor_name: '',
  iban: '',
  bic: '',
  bank_name: '',
  bank_country: '',
  titulaire: '',
  clearing_code: '',
  intermediary_bic: '',
  adresse_rue: '',
  adresse_cp: '',
  adresse_ville: '',
  adresse_etat: '',
  adresse_pays: '',
};

function BeneficiaireFormModal({
  existing,
  onClose,
}: {
  existing: Beneficiaire | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<BeneficiaireWrite>(() =>
    existing
      ? {
          type: existing.type,
          vendor_id: existing.vendor_id ?? null,
          vendor_name: existing.vendor_name,
          iban: existing.iban,
          bic: existing.bic ?? '',
          bank_name: existing.bank_name ?? '',
          bank_country: existing.bank_country ?? '',
          titulaire: existing.titulaire ?? '',
          clearing_code: existing.clearing_code ?? '',
          intermediary_bic: existing.intermediary_bic ?? '',
          adresse_rue: existing.adresse_rue ?? '',
          adresse_cp: existing.adresse_cp ?? '',
          adresse_ville: existing.adresse_ville ?? '',
          adresse_etat: existing.adresse_etat ?? '',
          adresse_pays: existing.adresse_pays ?? '',
        }
      : { ...EMPTY },
  );
  const [intlOpen, setIntlOpen] = useState(
    !!(existing?.clearing_code || existing?.intermediary_bic || existing?.adresse_etat),
  );
  const isClient = (form.type ?? 'fournisseur') === 'client';
  const vendorsQ = useZohoVendors();
  const vendors = vendorsQ.data?.vendors ?? [];

  // Les CLIENTS se cherchent côté serveur, pas en chargeant la liste entière :
  // l'organisation en compte plus de dix mille, et notre pagination s'arrêtait
  // à 10 000 — soit, par ordre alphabétique, tout ce qui suit la lettre I.
  // WATCHDOG était introuvable pour cette seule raison.
  const [rechercheClient, setRechercheClient] = useState('');
  const rechercheDifferee = useDebounce(rechercheClient, 250);
  const customersQ = useZohoCustomersSearch(rechercheDifferee, isClient);
  // Le client déjà choisi doit rester affiché même quand la recherche change :
  // le sélecteur retrouve son libellé dans la liste qu'on lui passe.
  const [clientChoisi, setClientChoisi] = useState<ZohoVendor | null>(null);
  const customers = useMemo(() => {
    const trouves = customersQ.data?.customers ?? [];
    if (!clientChoisi || trouves.some((c) => c.id === clientChoisi.id)) return trouves;
    return [clientChoisi, ...trouves];
  }, [customersQ.data, clientChoisi]);

  const mutation = useMutation({
    mutationFn: () =>
      existing
        ? beneficiairesApi.update(existing.id, form)
        : beneficiairesApi.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['beneficiaires'] });
      qc.invalidateQueries({ queryKey: ['paiements'] });
      qc.invalidateQueries({ queryKey: ['remboursements'] });
      toast.success(existing ? 'Bénéficiaire modifié.' : 'Bénéficiaire créé.');
      onClose();
    },
    onError: async (err) => {
      // Anti-doublon par fournisseur Zoho : le backend renvoie 409 reason=vendor_id.
      // On propose de mettre à jour le bénéficiaire existant plutôt que de créer.
      if (!existing && err instanceof ApiError && err.status === 409) {
        const body = err.body as
          | { reason?: string; existing_id?: string; existing_iban?: string }
          | null;
        if (body?.reason === 'vendor_id' && body.existing_id) {
          const ok = window.confirm(
            `${formatApiError(err)}.\nIBAN existant : ${body.existing_iban ?? '?'}\n\nMettre à jour ce bénéficiaire avec les informations saisies ?`,
          );
          if (ok) {
            try {
              await beneficiairesApi.update(body.existing_id, form);
              qc.invalidateQueries({ queryKey: ['beneficiaires'] });
              qc.invalidateQueries({ queryKey: ['paiements'] });
              qc.invalidateQueries({ queryKey: ['remboursements'] });
              toast.success('Bénéficiaire existant mis à jour.');
              onClose();
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }
          return;
        }
      }
      toast.error(formatApiError(err));
    },
  });

  const canSubmit =
    form.vendor_name.trim().length > 0 && form.iban.trim().length > 0 && !mutation.isPending;

  function set<K extends keyof BeneficiaireWrite>(k: K, v: BeneficiaireWrite[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-5 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold">
          {existing ? 'Modifier le bénéficiaire' : 'Nouveau bénéficiaire'}
        </h2>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Type *">
            <select
              value={form.type ?? 'fournisseur'}
              onChange={(e) => {
                // Change de type → on réinitialise le lien Zoho (vendor vs customer).
                const t = e.target.value as 'fournisseur' | 'client';
                setForm((f) => ({ ...f, type: t, vendor_id: null }));
              }}
              className={`${inputCls} bg-white`}
            >
              <option value="fournisseur">Fournisseur</option>
              <option value="client">Client</option>
            </select>
          </Field>
          <div className="hidden md:block" />
          {/* Lien Zoho conditionnel : fournisseur OU client selon le type.
              On ne lie QUE l'id ; le nom du bénéficiaire reste indépendant
              (le RIB peut afficher un nom différent, ex. entrepreneurs indiv.). */}
          <Field
            label={isClient ? 'Client Zoho Books' : 'Fournisseur Zoho Books'}
            className="md:col-span-2"
          >
            <VendorSelect
              vendors={isClient ? customers : vendors}
              nomDeLObjet={isClient ? 'client' : 'fournisseur'}
              value={form.vendor_id ?? null}
              placeholder={
                isClient
                  ? customersQ.isFetching
                    ? 'Recherche…'
                    : 'Taper au moins 2 lettres pour chercher un client'
                  : vendorsQ.isLoading
                    ? 'Chargement des fournisseurs…'
                    : 'Rechercher un fournisseur Zoho…'
              }
              onQueryChange={isClient ? setRechercheClient : undefined}
              onChange={(id) => {
                setForm((f) => ({ ...f, vendor_id: id }));
                if (isClient) setClientChoisi(customers.find((c) => c.id === id) ?? null);
              }}
            />
            <span className="text-xs text-slate-400">
              Lie le bénéficiaire à un {isClient ? 'client' : 'fournisseur'} Zoho
              (fiabilise le rapprochement). Le nom du bénéficiaire reste indépendant.
            </span>
          </Field>
          <Field label="Nom *" className="md:col-span-2">
            <input autoFocus value={form.vendor_name} onChange={(e) => set('vendor_name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="IBAN *" className="md:col-span-2">
            <IbanInput value={form.iban} onChange={(v) => set('iban', v)} placeholder="FR76 …" className={`${inputCls} font-mono`} />
          </Field>
          <Field label="BIC / SWIFT">
            <input value={form.bic ?? ''} onChange={(e) => set('bic', e.target.value)} className={`${inputCls} font-mono`} />
          </Field>
          <Field label="Nom de la banque">
            <input value={form.bank_name ?? ''} onChange={(e) => set('bank_name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Pays banque (2 lettres)">
            <input value={form.bank_country ?? ''} onChange={(e) => set('bank_country', e.target.value)} placeholder="FR" maxLength={2} className={inputCls} />
          </Field>
          <Field label="Titulaire du compte">
            <input value={form.titulaire ?? ''} onChange={(e) => set('titulaire', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Adresse (rue)" className="md:col-span-2">
            <input value={form.adresse_rue ?? ''} onChange={(e) => set('adresse_rue', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Code postal">
            <input value={form.adresse_cp ?? ''} onChange={(e) => set('adresse_cp', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Ville">
            <input value={form.adresse_ville ?? ''} onChange={(e) => set('adresse_ville', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Pays (2 lettres)">
            <input value={form.adresse_pays ?? ''} onChange={(e) => set('adresse_pays', e.target.value)} placeholder="FR" maxLength={2} className={inputCls} />
          </Field>
        </div>

        {/* Coordonnées internationales (hors SEPA) : repliable */}
        <div className="mt-3 border-t border-slate-200 pt-3">
          <button
            type="button"
            onClick={() => setIntlOpen((o) => !o)}
            className="text-sm text-slate-600 hover:text-slate-900"
          >
            {intlOpen ? '▾' : '▸'} Coordonnées internationales (hors SEPA)
          </button>
          {intlOpen && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Code de compensation local">
                <input value={form.clearing_code ?? ''} onChange={(e) => set('clearing_code', e.target.value)} placeholder="sort code / ABA…" className={`${inputCls} font-mono`} />
              </Field>
              <Field label="BIC banque intermédiaire">
                <input value={form.intermediary_bic ?? ''} onChange={(e) => set('intermediary_bic', e.target.value)} className={`${inputCls} font-mono`} />
              </Field>
              <Field label="État / province / région" className="md:col-span-2">
                <input value={form.adresse_etat ?? ''} onChange={(e) => set('adresse_etat', e.target.value)} className={inputCls} />
              </Field>
              <p className="md:col-span-2 text-xs text-slate-400">
                Ces champs sont conservés pour les comptes hors zone SEPA. L'export XML
                actuel reste SEPA (EUR/IBAN).
              </p>
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={mutation.isPending} className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100">
            Annuler
          </button>
          <button type="button" onClick={() => mutation.mutate()} disabled={!canSubmit} className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2">
            {mutation.isPending && <Spinner />}
            {existing ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal import CSV ────────────────────────────────────────────────────────

function ImportModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [preview, setPreview] = useState<ImportPreviewRow[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<ImportResult | null>(null);
  const [importType, setImportType] = useState<'fournisseur' | 'client'>('fournisseur');

  const previewMutation = useMutation({
    mutationFn: (csv: string) => beneficiairesApi.importPreview(csv),
    onSuccess: (resp) => {
      setPreview(resp.rows);
      // Pré-coche les lignes importables (valides, non doublons).
      setSelected(
        new Set(
          resp.rows.filter((r) => r.valid && !r.duplicate_in_db && !r.duplicate_in_file).map((r) => r.index),
        ),
      );
    },
    onError: (err) => toast.error(`Aperçu impossible : ${formatApiError(err)}`),
  });

  const importMutation = useMutation({
    mutationFn: (rows: BeneficiaireWrite[]) => beneficiairesApi.import(rows),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ['beneficiaires'] });
      qc.invalidateQueries({ queryKey: ['paiements'] });
      qc.invalidateQueries({ queryKey: ['remboursements'] });
      toast.success(`${res.imported} importé(s), ${res.skipped} ignoré(s).`);
    },
    onError: (err) => toast.error(`Import échoué : ${formatApiError(err)}`),
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  async function processFile(file: File) {
    if (!file) return;
    const okByName = /\.(csv|xlsx|xls)$/i.test(file.name);
    const okByType = /csv|sheet|excel/i.test(file.type);
    if (!okByName && !okByType) {
      toast.error('Format non supporté : choisis un fichier .csv ou .xlsx.');
      return;
    }
    setResult(null);
    try {
      let csv: string;
      if (/\.xlsx?$/i.test(file.name)) {
        // .xlsx/.xls → conversion en CSV côté navigateur (SheetJS, chargé à la
        // demande), puis on réutilise le pipeline d'import CSV existant.
        const XLSX = await import('xlsx');
        const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]!];
        csv = ws ? XLSX.utils.sheet_to_csv(ws) : '';
      } else {
        csv = await file.text();
      }
      previewMutation.mutate(csv);
    } catch (err) {
      toast.error(`Lecture du fichier échouée : ${formatApiError(err)}`);
    }
  }

  // Modèle .xlsx (en-têtes AGICAP + exemples) téléchargé via SheetJS.
  async function downloadTemplate() {
    try {
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.aoa_to_sheet([[...AGICAP_HEADERS], ...TEMPLATE_EXAMPLE_ROWS]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bénéficiaires');
      XLSX.writeFile(wb, 'modele_beneficiaires.xlsx');
    } catch (err) {
      toast.error(`Génération du modèle échouée : ${formatApiError(err)}`);
    }
  }

  function toggle(index: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function doImport() {
    if (!preview) return;
    const rows: BeneficiaireWrite[] = preview
      .filter((r) => selected.has(r.index))
      .map((r) => ({
        type: importType,
        vendor_name: r.vendor_name,
        iban: r.iban,
        bic: r.bic,
        bank_name: r.bank_name,
        bank_country: r.bank_country,
        titulaire: r.titulaire,
        clearing_code: r.clearing_code,
        intermediary_bic: r.intermediary_bic,
        adresse_rue: r.adresse_rue,
        adresse_cp: r.adresse_cp,
        adresse_ville: r.adresse_ville,
        adresse_etat: r.adresse_etat,
        adresse_pays: r.adresse_pays,
      }));
    if (rows.length === 0) {
      toast.error('Aucune ligne sélectionnée.');
      return;
    }
    importMutation.mutate(rows);
  }

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-5 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold">Importer des bénéficiaires (.csv / .xlsx, format AGICAP)</h2>

        {/* Résultat final */}
        {result ? (
          <div className="mt-4 space-y-3">
            <div className="rounded-md bg-emerald-50 text-emerald-800 px-4 py-3 text-sm">
              ✓ {result.imported} bénéficiaire(s) importé(s) · {result.skipped} ignoré(s) (déjà en base)
            </div>
            {result.errors.length > 0 && (
              <div className="rounded-md bg-red-50 text-red-700 px-4 py-3 text-sm">
                {result.errors.length} erreur(s) :
                <ul className="list-disc ml-5 mt-1">
                  {result.errors.map((e, i) => (
                    <li key={i}>{e.vendor_name} : {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700">
                Fermer
              </button>
            </div>
          </div>
        ) : !preview ? (
          /* Étape 1 : upload */
          <div className="mt-4 space-y-3">
            <p className="text-sm text-slate-500">
              Importe un fichier <strong>.csv</strong> ou <strong>.xlsx</strong> (format AGICAP).
              Colonnes obligatoires : <em>Name</em> et <em>Account number</em> (IBAN) ;
              le reste est optionnel. Besoin d'un point de départ ?{' '}
              <button type="button" onClick={downloadTemplate} className="text-blue-600 hover:underline">
                Télécharger le modèle (.xlsx)
              </button>
            </p>

            <label className="flex items-center gap-2 text-sm">
              <span className="text-slate-600">Type des bénéficiaires importés :</span>
              <select
                value={importType}
                onChange={(e) => setImportType(e.target.value as 'fournisseur' | 'client')}
                className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="fournisseur">Fournisseurs</option>
                <option value="client">Clients</option>
              </select>
            </label>

            {/* Zone de glisser-déposer (cliquable) */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  inputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragActive(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                const f = e.dataTransfer.files?.[0];
                if (f) void processFile(f);
              }}
              className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/40'
              }`}
            >
              {previewMutation.isPending ? (
                <span className="text-sm text-slate-600 inline-flex items-center gap-2">
                  <Spinner /> Analyse du fichier…
                </span>
              ) : (
                <>
                  <span className="text-3xl" aria-hidden>
                    📄
                  </span>
                  <span className="text-sm font-medium text-slate-700">
                    Glissez-déposez votre fichier ici
                  </span>
                  <span className="text-xs text-slate-500">
                    ou <span className="text-blue-600 underline">parcourez</span> vos fichiers : .csv ou .xlsx
                  </span>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void processFile(f);
                  e.target.value = ''; // autorise la re-sélection du même fichier
                }}
              />
            </div>
          </div>
        ) : (
          /* Étape 2 : aperçu + sélection */
          <div className="mt-4 space-y-3">
            <div className="text-sm text-slate-600">
              {preview.length} ligne(s) · {selected.size} sélectionnée(s) ·{' '}
              {preview.filter((r) => r.duplicate_in_db).length} déjà en base ·{' '}
              {preview.filter((r) => !r.valid).length} invalide(s)
            </div>
            <div className="border border-slate-200 rounded-md overflow-auto max-h-[50vh]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase sticky top-0">
                  <tr>
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 font-medium">Nom</th>
                    <th className="px-3 py-2 font-medium">IBAN</th>
                    <th className="px-3 py-2 font-medium">BIC</th>
                    <th className="px-3 py-2 font-medium">État</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {preview.map((r) => {
                    const blocked = !r.valid || r.duplicate_in_db || r.duplicate_in_file;
                    return (
                      <tr key={r.index} className={blocked ? 'bg-slate-50/60' : ''}>
                        <td className="px-3 py-1.5">
                          <input
                            type="checkbox"
                            checked={selected.has(r.index)}
                            disabled={!r.valid}
                            onChange={() => toggle(r.index)}
                          />
                        </td>
                        <td className="px-3 py-1.5">{r.vendor_name || <span className="text-red-500 italic">(sans nom)</span>}</td>
                        <td className="px-3 py-1.5 font-mono text-xs">{r.iban ? fmtIban(r.iban) : '-'}</td>
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-500">{r.bic ?? '-'}</td>
                        <td className="px-3 py-1.5 text-xs">
                          {!r.valid ? (
                            <span className="text-[#C0423F]">{r.errors.join(', ')}</span>
                          ) : r.duplicate_in_db ? (
                            <span className="text-amber-600">Déjà en base</span>
                          ) : r.duplicate_in_file ? (
                            <span className="text-amber-600">Doublon dans le fichier</span>
                          ) : (
                            <span className="text-emerald-600">Prêt</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} disabled={importMutation.isPending} className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
              <button type="button" onClick={doImport} disabled={selected.size === 0 || importMutation.isPending} className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2">
                {importMutation.isPending && <Spinner />}
                Importer {selected.size} bénéficiaire(s)
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls =
  'rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

/**
 * Champ IBAN qui affiche la saisie groupée par 4 (FR76 1870 7000 …) pour
 * faciliter la relecture. Stocke la valeur normalisée (majuscules, sans
 * espaces) et repositionne le curseur après reformatage (édition au milieu OK).
 */
function IbanInput({
  value,
  onChange,
  className,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  placeholder?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const fmt = (s: string) =>
    s.replace(/[^A-Za-z0-9]/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();

  return (
    <input
      ref={ref}
      value={fmt(value)}
      placeholder={placeholder}
      className={className}
      inputMode="text"
      autoCapitalize="characters"
      spellCheck={false}
      onChange={(e) => {
        const el = e.currentTarget;
        const sel = el.selectionStart ?? el.value.length;
        const beforeCount = el.value.slice(0, sel).replace(/[^A-Za-z0-9]/g, '').length;
        const normalized = el.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        onChange(normalized);
        // Repositionne le curseur après `beforeCount` caractères alphanumériques
        // dans la version reformatée (compense les espaces insérés).
        const formatted = fmt(normalized);
        let pos = 0;
        let count = 0;
        for (let i = 0; i < formatted.length && count < beforeCount; i++) {
          if (formatted[i] !== ' ') count++;
          pos = i + 1;
        }
        requestAnimationFrame(() => ref.current?.setSelectionRange(pos, pos));
      }}
    />
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-1 text-sm ${className ?? ''}`}>
      <span className="font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
